/**
 * V2ListItemEditor - Card editor for a single V2 options list item
 *
 * Cloned from V1 ListItemEditor with V2-specific additions:
 * - variableValue display + inline editing (pencil icon toggle)
 * - Uses V2 path-based onChange pattern
 *
 * Layout:
 * [#N]                              [Delete]
 * [Image - full width]
 * [Title - full width]
 * [variableValue tag + pencil]
 * [Subtitle - full width]
 *
 * Performance: memo + local state + blur sync (same as V1)
 */

import React, { useCallback, useState, useEffect, memo } from 'react';
import { Box, Typography, IconButton, Tooltip, FormControl, Select, MenuItem } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { StyledTextField, MediaUploadField } from '@modules/ui-components';
import { FIELD_GAP_SMALL, SPACING } from '../../../../config/ui';

export interface V2ListItemEditorProps {
  /** Item index (0-based) */
  index: number;

  /** Current title value */
  title: string;

  /** Current subtitle value */
  subtitle: string;

  /** Current image URL (null = no image) */
  imageUrl: string | null;

  /** Current variableValue for this item */
  variableValue: string | null;

  /** Available variable options for dropdown (from bound variable) */
  variableOptions?: Array<{ id: string; label: string }>;

  /** Visibility flags for subtitle / image */
  showSubtitle: boolean;
  showImage: boolean;

  /** Whether to show variableValue (selection screens only) */
  showVariableValue: boolean;

  /** Callback when title changes (blur sync) */
  onTitleChange: (value: string) => void;

  /** Callback when subtitle changes (blur sync) */
  onSubtitleChange?: (value: string) => void;

  /** Callback when image changes */
  onImageChange?: (value: string | null) => void;

  /** Callback for image upload - returns URL */
  onImageUpload?: (file: File) => Promise<string>;

  /** Callback when variableValue changes. Returns error string on validation failure, null on success. */
  onVariableValueChange?: (value: string) => string | null;

  /** Callback when delete button is clicked */
  onDelete?: () => void;

  /** Whether editing is disabled */
  disabled?: boolean;
}

const CUSTOM_VALUE_SENTINEL = '__CUSTOM__';

const V2ListItemEditorComponent: React.FC<V2ListItemEditorProps> = ({
  index,
  title,
  subtitle,
  imageUrl,
  variableValue,
  variableOptions,
  showSubtitle,
  showImage,
  showVariableValue,
  onTitleChange,
  onSubtitleChange,
  onImageChange,
  onImageUpload,
  onVariableValueChange,
  onDelete,
  disabled = false,
}) => {
  // Local state for text fields - prevents focus loss on parent re-render
  const [localTitle, setLocalTitle] = useState(title);
  const [localSubtitle, setLocalSubtitle] = useState(subtitle);

  // Inline editing state for variableValue
  const [editingVarValue, setEditingVarValue] = useState(false);
  const [localVarValue, setLocalVarValue] = useState(variableValue || `item_${index}`);
  const [customVarInput, setCustomVarInput] = useState(false);
  const [varValueError, setVarValueError] = useState('');

  // Sync props to local state when external changes occur (e.g., reorder, undo)
  useEffect(() => { setLocalTitle(title); }, [title]);
  useEffect(() => { setLocalSubtitle(subtitle); }, [subtitle]);
  useEffect(() => { setLocalVarValue(variableValue || `item_${index}`); }, [variableValue, index]);

  // Title: local input + blur sync
  const handleTitleInput = useCallback((value: string) => { setLocalTitle(value); }, []);
  const handleTitleBlur = useCallback(() => {
    if (localTitle !== title) onTitleChange(localTitle);
  }, [localTitle, title, onTitleChange]);

  // Subtitle: local input + blur sync
  const handleSubtitleInput = useCallback((value: string) => { setLocalSubtitle(value); }, []);
  const handleSubtitleBlur = useCallback(() => {
    if (localSubtitle !== subtitle && onSubtitleChange) onSubtitleChange(localSubtitle);
  }, [localSubtitle, subtitle, onSubtitleChange]);

  // Image change handler (File or URL)
  const handleImageChange = useCallback(
    async (value: string | File | null) => {
      if (!onImageChange) return;
      if (value instanceof File) {
        if (onImageUpload) {
          try {
            const url = await onImageUpload(value);
            onImageChange(url);
          } catch (error) {
            console.error('Image upload failed:', error);
          }
        }
      } else {
        onImageChange(value);
      }
    },
    [onImageChange, onImageUpload]
  );

  // VariableValue: toggle edit mode
  const handleStartEditVarValue = useCallback(() => {
    setLocalVarValue(variableValue || `item_${index}`);
    setCustomVarInput(false);
    setVarValueError('');
    setEditingVarValue(true);
  }, [variableValue, index]);

  const handleConfirmVarValue = useCallback(() => {
    const trimmed = localVarValue.trim();
    if (!trimmed) return;

    if (onVariableValueChange) {
      const err = onVariableValueChange(trimmed);
      if (err) {
        setVarValueError(err);
        return; // block confirmation — stay in edit mode
      }
    }
    setVarValueError('');
    setEditingVarValue(false);
    setCustomVarInput(false);
  }, [localVarValue, onVariableValueChange]);

  // Dropdown selection handler
  const handleDropdownSelect = useCallback((event: SelectChangeEvent<string>) => {
    const val = event.target.value;
    if (val === CUSTOM_VALUE_SENTINEL) {
      setCustomVarInput(true);
      setVarValueError('');
      return;
    }
    if (onVariableValueChange) {
      const err = onVariableValueChange(val);
      if (err) {
        // Switch to custom input to show the error
        setCustomVarInput(true);
        setLocalVarValue(val);
        setVarValueError(err);
        return;
      }
    }
    setVarValueError('');
    setEditingVarValue(false);
  }, [onVariableValueChange]);

  const hasDropdownOptions = variableOptions && variableOptions.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        pt: 0.5,
        px: 1.5,
        pb: 1.5,
        bgcolor: 'background.paper',
        borderRadius: SPACING.sm,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'action.hover',
        },
      }}
      data-testid={`v2-list-item-editor-${index}`}
    >
      {/* Header row: variableValue (or #N fallback) + delete */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          {/* Spacer for SortableList drag handle (absolutely positioned at left:40) */}
          <Box sx={{ width: 28, flexShrink: 0 }} />
          {showVariableValue && editingVarValue ? (
            hasDropdownOptions && !customVarInput ? (
              // Dropdown mode — show bound variable's existing options
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  value={localVarValue}
                  onChange={handleDropdownSelect}
                  disabled={disabled}
                  displayEmpty
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                  data-testid={`v2-list-item-varvalue-select-${index}`}
                >
                  {variableOptions!.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({opt.id})
                      </Typography>
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_VALUE_SENTINEL}>
                    <em>Custom value...</em>
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              // Custom/fallback text mode
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StyledTextField
                    value={localVarValue}
                    onChange={(val: string) => {
                      setLocalVarValue(val);
                      if (varValueError) setVarValueError('');
                    }}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    placeholder="variable value"
                    error={!!varValueError}
                    data-testid={`v2-list-item-varvalue-input-${index}`}
                  />
                  <IconButton size="small" onClick={handleConfirmVarValue} sx={{ p: 0.25 }} data-testid={`v2-list-item-varvalue-confirm-${index}`}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Box>
                {varValueError && (
                  <Typography variant="caption" color="error" sx={{ pl: 0.5, mt: 0.25 }}>
                    {varValueError}
                  </Typography>
                )}
              </Box>
            )
          ) : showVariableValue ? (
            <>
              <Typography
                variant="caption"
                data-testid={`v2-list-item-varvalue-${index}`}
                sx={{
                  color: 'text.secondary',
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  bgcolor: 'action.hover',
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {variableValue || `item_${index}`}
              </Typography>
              <IconButton
                size="small"
                onClick={handleStartEditVarValue}
                disabled={disabled}
                sx={{ p: 0.25, flexShrink: 0 }}
                data-testid={`v2-list-item-varvalue-edit-${index}`}
              >
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ width: 24, flexShrink: 0 }}>
              #{index + 1}
            </Typography>
          )}
        </Box>

        {onDelete && (
          <Tooltip title="Delete item">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled}
                sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' }, flexShrink: 0 }}
                data-testid={`v2-list-item-delete-${index}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Image row */}
      {showImage && (
        <Box>
          <MediaUploadField
            value={imageUrl || null}
            onChange={handleImageChange}
            mediaType="image"
            disabled={disabled}
            size="small"
            showPreview
            placeholder="Image"
          />
        </Box>
      )}

      {/* Title field */}
      <StyledTextField
        value={localTitle}
        onChange={handleTitleInput}
        onBlur={handleTitleBlur}
        disabled={disabled}
        size="small"
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        placeholder={`Item ${index + 1} title`}
        data-testid={`v2-list-item-title-${index}`}
      />

      {/* Subtitle field */}
      {showSubtitle && onSubtitleChange && (
        <StyledTextField
          value={localSubtitle}
          onChange={handleSubtitleInput}
          onBlur={handleSubtitleBlur}
          disabled={disabled}
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          placeholder="Subtitle"
          data-testid={`v2-list-item-subtitle-${index}`}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              color: 'text.secondary',
            },
          }}
        />
      )}
    </Box>
  );
};

export const V2ListItemEditor = memo(V2ListItemEditorComponent);
export default V2ListItemEditor;
